import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Share, Download } from "lucide-react";

function isInstalled() {
  try {
    return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
  } catch { return false; }
}

function isIphone() {
  const ua = window.navigator.userAgent || "";
  const iPadOs = ua.includes("Macintosh") && window.navigator.maxTouchPoints > 1;
  return /iPhone|iPad|iPod/.test(ua) || iPadOs;
}

/** Gold "Install app" button for the top bar. Hidden once the site is installed or when the browser can't install it. */
export function InstallButton() {
  const [evt, setEvt] = useState(null);
  const [ios, setIos] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [help, setHelp] = useState(false);

  useEffect(() => {
    if (isInstalled()) { setInstalled(true); return undefined; }
    setIos(isIphone());
    const onPrompt = (e) => { e.preventDefault(); setEvt(e); };
    const onInstalled = () => { setInstalled(true); setEvt(null); setHelp(false); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  useEffect(() => {
    if (!help) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setHelp(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [help]);

  if (installed || (!evt && !ios)) return null;

  const click = async () => {
    if (evt) {
      evt.prompt();
      try { await evt.userChoice; } catch { /* ignore */ }
      setEvt(null);
    } else {
      setHelp(true);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={click}
        data-testid="install-app"
        aria-label="Install the My Theatre app"
        title="Install the app"
        className="inline-flex items-center gap-1.5 rounded-full bg-[#C89B3C] p-2 text-sm font-semibold text-black transition-colors hover:bg-[#d8ab4a]"
      >
        <Download className="h-4 w-4" aria-hidden="true" />
      </button>

      {help && createPortal(
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60 p-3 sm:items-center" role="dialog" aria-modal="true" aria-label="Install My Theatre" data-testid="install-help" onClick={() => setHelp(false)}>
          <div className="w-full max-w-sm rounded-2xl border border-amber-900/30 bg-[#161118] p-5 text-amber-100 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <h2 className="font-serif text-xl font-semibold">Get the My Theatre app</h2>
              <button type="button" onClick={() => setHelp(false)} aria-label="Close" data-testid="install-help-close" className="rounded-full p-1.5 text-amber-100/60 hover:bg-white/10"><X className="h-4 w-4" /></button>
            </div>
            <ol className="mt-4 space-y-3 text-sm text-amber-100/80">
              <li className="flex gap-3"><span className="font-semibold text-[#C89B3C]">1</span><span>Tap <Share className="mx-0.5 inline h-4 w-4 align-text-bottom text-[#C89B3C]" aria-label="Share" /> <strong className="text-amber-50">Share</strong> in your browser's toolbar, then choose <strong className="text-amber-50">Add to Home Screen</strong>.</span></li>
              <li className="flex gap-3"><span className="font-semibold text-[#C89B3C]">2</span><span>Tap <strong className="text-amber-50">Add</strong>. That's it, My Theatre is now on your home screen.</span></li>
            </ol>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}